import { useState, useCallback, useEffect, useRef } from 'react';
import { useRoomContext, useLocalParticipant } from '@livekit/components-react';
import { RoomEvent, DataPacket_Kind } from 'livekit-client';
import { useTranslation } from '../../hooks/useTranslation';

interface ChatMessage {
  id: string;
  sender: string;
  text: string;
  timestamp: number;
}

const encoder = new TextEncoder();
const decoder = new TextDecoder();

export function ChatPanel() {
  const { t } = useTranslation();
  const room = useRoomContext();
  const { localParticipant } = useLocalParticipant();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleData = (payload: Uint8Array) => {
      try {
        const data = JSON.parse(decoder.decode(payload));
        if (data.type === 'chat') {
          setMessages((prev) => [...prev, {
            id: `${Date.now()}-${Math.random()}`,
            sender: data.sender,
            text: data.text,
            timestamp: data.timestamp,
          }]);
        }
      } catch {
        // Ignore non-chat data
      }
    };

    room.on(RoomEvent.DataReceived, handleData);
    return () => {
      room.off(RoomEvent.DataReceived, handleData);
    };
  }, [room]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = useCallback(() => {
    const text = inputText.trim();
    if (!text) return;

    const payload = {
      type: 'chat',
      sender: localParticipant.name || localParticipant.identity,
      text,
      timestamp: Date.now(),
    };

    const data = encoder.encode(JSON.stringify(payload));
    localParticipant.publishData(data, { reliable: true });

    // Add to own messages
    setMessages((prev) => [...prev, {
      id: `${Date.now()}-local`,
      sender: payload.sender,
      text,
      timestamp: payload.timestamp,
    }]);

    setInputText('');
  }, [inputText, localParticipant]);

  return (
    <div className="videocall-chat">
      <div className="videocall-chat__messages">
        {messages.map((msg) => (
          <div key={msg.id} className="videocall-chat__message">
            <div className="videocall-chat__message-sender">{msg.sender}</div>
            <div>{msg.text}</div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
      <div className="videocall-chat__input-area">
        <input
          className="videocall-chat__input"
          type="text"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
          placeholder={t('videocall.typeMessage')}
        />
        <button className="videocall-chat__send-btn" onClick={sendMessage}>
          {t('common.send')}
        </button>
      </div>
    </div>
  );
}
